import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Upload, FileArrowUp, CheckCircle, XCircle, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImportPODPage = () => {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setResult(null);
      } else {
        toast.error('Formato file non supportato. Usa CSV.');
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setResult(null);
    } else {
      toast.error('Formato file non supportato. Usa CSV.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Seleziona un file da importare');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axiosInstance.post('/pods/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(data);

      if (data.success) {
        toast.success(`Importazione completata! ${data.imported} POD importati.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante importazione');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">Import Anagrafica POD</h1>
        <p className="mt-2 text-sm text-slate-600">
          Carica file CSV con i punti di fornitura previsti per l'attivazione
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-blue-900">Formato File Richiesto (CSV con separatore ;)</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(`${API_URL}/api/pods/template`, '_blank')}
            className="rounded-sm text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
            data-testid="download-pod-template"
          >
            <DownloadSimple size={14} className="mr-1" />
            Scarica Template
          </Button>
        </div>
        <p className="text-xs text-blue-700 mb-2">Il file deve contenere le seguenti colonne:</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
            <li><strong>Codice</strong> - Codice POD (es: IT001E98036799)</li>
            <li><strong>Cliente</strong> - Ragione sociale / nome</li>
            <li><strong>Partita IVA</strong> - P.IVA cliente</li>
            <li><strong>Codice Fiscale</strong> - CF cliente</li>
            <li><strong>Tipologia d'uso</strong> - "Domestico" o "Altri usi"</li>
            <li><strong>Provincia Fornitura</strong> - Sigla (es: RM, MI, OT)</li>
            <li><strong>Toponimo</strong> - Via, Piazza, Viale, ecc.</li>
          </ul>
          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
            <li><strong>Indirizzo</strong> - Nome strada</li>
            <li><strong>Civico</strong> - Numero civico</li>
            <li><strong>CAP</strong> - Codice avviamento postale</li>
            <li><strong>Comune</strong> - Comune di fornitura</li>
            <li><strong>Codice ISTAT</strong> - Codice ISTAT comune</li>
            <li><strong>Data Inizio</strong> - Data attivazione (YYYY-MM-DD)</li>
            <li><strong>Data Fine</strong> - Data cessazione (2099-12-31 se attivo)</li>
            <li><strong>Consumo Annuo</strong> - Consumo kWh annuo stimato</li>
          </ul>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-sm p-4">
        <h3 className="text-sm font-semibold text-green-900 mb-2">Calcolo Automatico Accise</h3>
        <div className="grid grid-cols-2 gap-4 text-xs text-green-700">
          <div>
            <p className="font-medium text-green-800">Altri usi (J2)</p>
            <p>Aliquota: 0,0125 EUR/kWh</p>
            <p>Imposta = Consumo x 0,0125</p>
          </div>
          <div>
            <p className="font-medium text-green-800">Uso domestico (J1)</p>
            <p>Aliquota: 0,0227 EUR/kWh</p>
            <p>Esenzione primi 150 kWh/mese (1.800 kWh/anno)</p>
            <p>Imposta = max(0, Consumo - 1.800) x 0,0227</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-8">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-slate-300 rounded-sm p-12 text-center hover:border-[#0066FF] hover:bg-slate-50 transition-all cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          data-testid="file-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            data-testid="file-input"
          />

          {file ? (
            <div>
              <FileArrowUp size={48} className="mx-auto text-green-600 mb-4" />
              <p className="text-sm font-medium text-slate-900 mb-1">{file.name}</p>
              <p className="text-xs text-slate-600">{(file.size / 1024).toFixed(2)} KB</p>
            </div>
          ) : (
            <div>
              <Upload size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-sm font-medium text-slate-900 mb-1">
                Trascina il file CSV qui o clicca per selezionare
              </p>
              <p className="text-xs text-slate-600">CSV (Max 10 MB)</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-sm"
            data-testid="upload-button"
          >
            {uploading ? 'Importazione in corso...' : 'Avvia Importazione'}
          </Button>
          {file && (
            <Button onClick={handleReset} variant="outline" className="rounded-sm">
              Reset
            </Button>
          )}
        </div>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="import-result">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">
            Risultato Importazione
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={20} className="text-green-600" weight="fill" />
                <span className="text-xs tracking-[0.2em] uppercase font-semibold text-green-700">Importati</span>
              </div>
              <p className="text-3xl font-bold text-green-900">{result.imported}</p>
            </div>

            <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <XCircle size={20} className="text-red-600" weight="fill" />
                <span className="text-xs tracking-[0.2em] uppercase font-semibold text-red-700">Falliti</span>
              </div>
              <p className="text-3xl font-bold text-red-900">{result.failed}</p>
            </div>
          </div>

          {result.batch_id && (
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm mb-4">
              <p className="text-xs text-slate-600">
                Batch ID: <span className="font-mono">{result.batch_id}</span>
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Confermi annullamento di questo caricamento? Tutti i POD importati verranno eliminati.')) return;
                  try {
                    const { data } = await axiosInstance.delete(`/pods/batch/${result.batch_id}`);
                    toast.success(`${data.deleted} POD eliminati`);
                    setResult(null);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Errore annullamento');
                  }
                }}
                className="rounded-sm text-xs text-red-600 border-red-200 hover:bg-red-50"
                data-testid="rollback-import"
              >
                Annulla questo caricamento
              </Button>
            </div>
          )}

          {result.errors && result.errors.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Errori:</h3>
              <div className="space-y-1">
                {result.errors.map((error, idx) => (
                  <p key={`err-${idx}-${error.slice(0, 20)}`} className="text-xs text-red-600">{error}</p>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/pods/active')}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-sm"
            >
              Visualizza POD
            </Button>
            <Button onClick={handleReset} variant="outline" className="rounded-sm">
              Importa Altro File
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportPODPage;